import { z } from 'zod';
import { CIDR_RE, isoDate, objectId, optional, requiredText, text } from './common.js';

/**
 * Mall / landlord sales-sharing integrations (spec §9). Created and managed by super admins only
 * (admin portal), at one of three levels:
 *   client   every outlet of the client (current and future)
 *   outlets  a chosen group of outlets
 *   outlet   one outlet
 */
export const INTEGRATION_LEVELS = Object.freeze([
  { key: 'client', label: 'Whole client (all outlets)' },
  { key: 'outlets', label: 'Group of outlets' },
  { key: 'outlet', label: 'Single outlet' },
]);
export const INTEGRATION_SCOPES = Object.freeze([{ key: 'sales:read', label: 'Read sales figures' }]);
export const PUSH_MODES = Object.freeze([
  { key: 'off', label: 'Off (mall pulls only)' },
  { key: 'per_bill', label: 'Every bill, near real-time' },
  { key: 'hourly', label: 'Hourly batch' },
  { key: 'daily', label: 'Daily summary' },
]);
export const ADAPTERS = Object.freeze([
  { key: 'json', label: 'JSON (default format)' },
  { key: 'csv', label: 'CSV file' },
  { key: 'mapped', label: 'JSON with renamed fields (field map)' },
]);
export const WEBHOOK_EVENTS = Object.freeze([
  'bill.completed',
  'bill.voided',
  'bill.refunded',
  'sales.hourly',
  'sales.daily',
  'integration.test',
]);

const fieldName = z
  .string()
  .trim()
  .regex(/^[A-Za-z_][A-Za-z0-9_.]{0,63}$/, 'Letters, digits, _ and .');

export const integrationSchema = z
  .strictObject({
    name: requiredText(80),
    level: z.enum(INTEGRATION_LEVELS.map((l) => l.key)),
    outletIds: z.array(objectId).max(100).default([]),
    scopes: z
      .array(z.enum(INTEGRATION_SCOPES.map((s) => s.key)))
      .min(1, 'Choose at least one permission')
      .default(['sales:read']),
    ipAllowlist: z
      .array(z.string().trim().regex(CIDR_RE, 'Use an IP or CIDR, e.g. 203.0.113.0/24'))
      .max(20)
      .default([]),
    push: z
      .strictObject({
        mode: z.enum(PUSH_MODES.map((m) => m.key)).default('off'),
        url: optional(z.string().trim().url('Enter the full https:// address').max(500)),
        adapter: z.enum(ADAPTERS.map((a) => a.key)).default('json'),
        fieldMap: z.record(fieldName, fieldName).default({}),
      })
      .default({ mode: 'off', adapter: 'json', fieldMap: {} }),
    contact: optional(text(160)),
    notes: optional(text(500)),
  })
  .superRefine((v, ctx) => {
    if (v.level === 'outlet' && v.outletIds.length !== 1)
      ctx.addIssue({ code: 'custom', path: ['outletIds'], message: 'Choose exactly one outlet' });
    if (v.level === 'outlets' && v.outletIds.length < 1)
      ctx.addIssue({ code: 'custom', path: ['outletIds'], message: 'Choose the outlets' });
    if (v.push.mode !== 'off' && !v.push.url)
      ctx.addIssue({ code: 'custom', path: ['push', 'url'], message: 'Where should sales be sent?' });
    if (Object.keys(v.push.fieldMap).length > 40)
      ctx.addIssue({ code: 'custom', path: ['push', 'fieldMap'], message: 'At most 40 fields' });
  });

export const integrationStatusSchema = z.strictObject({
  status: z.enum(['active', 'disabled']),
  reason: optional(text(200)),
});

export const integrationRevokeSchema = z.strictObject({
  reason: requiredText(200),
  /** Type the integration's name to confirm (checked by the server). */
  confirmName: requiredText(80),
});

export const rotateSecretSchema = z.strictObject({
  /** Hours the previous secret keeps working, so the mall can switch without downtime. */
  overlapHours: z.coerce.number().int().min(0).max(168).default(24),
});

export const deliveryListQuery = z.strictObject({
  status: z.enum(['pending', 'delivered', 'failed']).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

// ---------------------------------------------------------------- public API (/api/public/v1)
export const tokenRequestSchema = z.strictObject({
  grant_type: z.literal('client_credentials'),
  client_id: z.string().trim().min(8).max(80),
  client_secret: z.string().trim().min(16).max(200),
  scope: optional(text(200)),
});

const MAX_RANGE_DAYS = 31;

/**
 * Which outlet: its code, as printed on its invoices (MDP1/2627/000214 → MDP1). Codes are unique
 * within a client and never change. outletId (from GET /outlets) is still accepted. Exactly one.
 */
const outletRef = {
  outletCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z][A-Z0-9]{1,3}$/, 'An outlet code, like MDP1 (see GET /outlets)')
    .optional(),
  outletId: objectId.optional(),
};
function oneOutlet(q, ctx) {
  if (!q.outletCode && !q.outletId)
    ctx.addIssue({ code: 'custom', path: ['outletCode'], message: 'Required (the outlet code, like MDP1)' });
  else if (q.outletCode && q.outletId)
    ctx.addIssue({ code: 'custom', path: ['outletId'], message: 'Give outletCode or outletId, not both' });
}

export const dailySummaryQuery = z.strictObject({ ...outletRef, date: isoDate }).superRefine(oneOutlet);
export const hourlyQuery = z.strictObject({ ...outletRef, date: isoDate }).superRefine(oneOutlet);
export const transactionsQuery = z
  .strictObject({
    ...outletRef,
    from: isoDate,
    to: isoDate,
    cursor: optional(z.string().trim().max(200)),
    limit: z.coerce.number().int().min(1).max(500).default(100),
  })
  .superRefine((q, ctx) => {
    oneOutlet(q, ctx);
    const days = (Date.parse(`${q.to}T00:00:00Z`) - Date.parse(`${q.from}T00:00:00Z`)) / 86_400_000;
    if (days < 0) ctx.addIssue({ code: 'custom', path: ['from'], message: 'from must not be after to' });
    if (days > MAX_RANGE_DAYS - 1)
      ctx.addIssue({ code: 'custom', path: ['to'], message: `At most ${MAX_RANGE_DAYS} days per request` });
  });
